import { useContext, useState } from 'react';
import { Theme, Typography, useMediaQuery } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useApolloClient } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { FunnelCart } from 'src/ts/types/cart';
import { centsToDollarsStr } from 'src/utils/currency';
import { useDiscount } from 'src/context/discount';
import UserContext from 'src/context/user';
import { removeDiscountFromCart } from '../_utils/discounts/set-discount';
import DiscountCodeInput from './DiscountCodeInput';

interface Props {
  cart: FunnelCart;
  setCart: React.Dispatch<React.SetStateAction<FunnelCart | null>>;
  discountTotal: number;
}

const DiscountCodeRow: React.FC<Props> = ({ cart, setCart, discountTotal }) => {
  const apolloClient = useApolloClient();
  const userContext = useContext(UserContext);
  const discountEligibility = useDiscount();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const theme = useTheme();

  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemovePromoCode = async () => {
    if (!userContext) return;

    setIsRemoving(true);

    const result = await removeDiscountFromCart({
      cart,
      apolloClient,
      userContext,
    });

    if (result.success) {
      setCart(result.cart);
    }

    setIsRemoving(false);
  };

  if (!discountEligibility.eligibleDiscount) {
    return <DiscountCodeInput cart={cart} setCart={setCart} />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body1">
            Promo code {isSmallScreen ? undefined : '- '}
          </Typography>
          {!isSmallScreen && (
            <DiscountTag
              code={discountEligibility.eligibleDiscount.discountCode}
              isRemoving={isRemoving}
              onRemove={handleRemovePromoCode}
            />
          )}
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.error.light,
            fontWeight: 'bold',
          }}
        >
          -{centsToDollarsStr(discountTotal)}
        </Typography>
      </Box>
      {isSmallScreen && (
        <DiscountTag
          code={discountEligibility.eligibleDiscount.discountCode}
          isRemoving={isRemoving}
          onRemove={handleRemovePromoCode}
        />
      )}
    </Box>
  );
};

function DiscountTag({
  code,
  isRemoving,
  onRemove,
}: {
  code: string;
  isRemoving: boolean;
  onRemove: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 'fit-content',
        gap: 0.5,
        backgroundColor: '#DDF8DF',
        border: '1px solid #BBF1BF',
        borderRadius: 1,
        px: 1,
        py: 0.25,
      }}
    >
      <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant="body2">{code}</Typography>
      <CloseIcon
        sx={{
          fontSize: 16,
          cursor: isRemoving ? 'default' : 'pointer',
          color: 'text.secondary',
          opacity: isRemoving ? 0.5 : 1,
        }}
        onClick={() => !isRemoving && onRemove()}
      />
    </Box>
  );
}

export default DiscountCodeRow;
